import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Baby,
  Ban,
  Bandage,
  Bone,
  Brain,
  CigaretteOff,
  CircleDot,
  Clock,
  Cloud,
  Droplets,
  Dna,
  Ear,
  Eye,
  Flame,
  Frown,
  HeartPulse,
  Hourglass,
  Microscope,
  Moon,
  Pill,
  Scissors,
  ShieldAlert,
  ShieldOff,
  Snowflake,
  Sparkles,
  Stethoscope,
  Syringe,
  Thermometer,
  Toilet,
  Utensils,
  Waves,
  Wind,
  Zap,
  type LucideIcon,
} from "lucide-react";

type IconPick = { icon: LucideIcon; tint: string };

// Tints rotate so adjacent cards stay visually distinct.
const TINTS = [
  "from-rose-500/15 to-rose-500/0 text-rose-600",
  "from-amber-500/15 to-amber-500/0 text-amber-600",
  "from-emerald-500/15 to-emerald-500/0 text-emerald-600",
  "from-sky-500/15 to-sky-500/0 text-sky-600",
  "from-violet-500/15 to-violet-500/0 text-violet-600",
  "from-orange-500/15 to-orange-500/0 text-orange-600",
  "from-cyan-500/15 to-cyan-500/0 text-cyan-600",
  "from-pink-500/15 to-pink-500/0 text-pink-600",
];

// Order matters — first match wins. Put specific terms before generic ones.
const KEYWORD_ICONS: { match: RegExp; icon: LucideIcon }[] = [
  // Body parts / specialties
  { match: /\b(eye|vision|sight|cornea|retina|cataract|blur|glaucoma)\b/i, icon: Eye },
  { match: /\b(ear|hearing|tinnitus|otitis)\b/i, icon: Ear },
  { match: /\b(nose|nasal|sinus|smell|snoring|congestion)\b/i, icon: Wind },
  { match: /\b(throat|tonsil|adenoid|voice|hoarse|swallow)\b/i, icon: Activity },
  { match: /\b(bone|fracture|joint|knee|hip|spine|cartilage|ortho)\b/i, icon: Bone },
  { match: /\b(brain|nerve|neuro|headache|migraine|dizzi)\b/i, icon: Brain },
  { match: /\b(heart|cardiac|chest pain|palpitation)\b/i, icon: HeartPulse },
  { match: /\b(baby|pregnan|infant|fetal|newborn|child|paediatric|pediatric|congenital)\b/i, icon: Baby },

  // Symptoms / sensations
  { match: /\b(burn|burning|hot|fever|temperature|inflamm)\b/i, icon: Flame },
  { match: /\b(cold|chill|numb)\b/i, icon: Snowflake },
  { match: /\b(bleed|blood|haemorr|hemorr|discharge|menstr|period|spotting)\b/i, icon: Droplets },
  { match: /\b(urin|urethr|bladder|frequency|incontin|leak|toilet|stool|bowel|constipat|diarrh)\b/i, icon: Toilet },
  { match: /\b(pain|ache|sore|cramp|tender|sharp|stabbing)\b/i, icon: Zap },
  { match: /\b(swell|lump|mass|bulge|cyst|tumor|tumour|nodule|growth)\b/i, icon: CircleDot },
  { match: /\b(itch|rash|redness|irrita)\b/i, icon: AlertTriangle },
  { match: /\b(infect|bacter|fungal|viral|sepsis|abscess|pus)\b/i, icon: ShieldAlert },
  { match: /\b(sleep|insomnia|fatigue|tired|drowsy|night)\b/i, icon: Moon },
  { match: /\b(stress|anxiety|depress|mood|sad|emotional)\b/i, icon: Frown },
  { match: /\b(stiff|mobility|movement|walk|difficult|limit)\b/i, icon: Clock },
  { match: /\b(water|fluid|swelling|edema|oedema|drain)\b/i, icon: Waves },

  // Causes
  { match: /\b(genetic|hereditary|family history|congenital|chromos)\b/i, icon: Dna },
  { match: /\b(diet|food|nutrition|obesity|weight|eat)\b/i, icon: Utensils },
  { match: /\b(lifestyle|sedentary|posture|exercise|activity)\b/i, icon: Activity },
  { match: /\b(smok|tobacco|alcohol|drug|substance)\b/i, icon: CigaretteOff },
  { match: /\b(age|aging|ageing|wear|tear|degener|elderly|older)\b/i, icon: Hourglass },
  { match: /\b(hormon|endocrine|thyroid|diabet|insulin|metabol)\b/i, icon: Pill },
  { match: /\b(injur|trauma|accident|fall|fracture|impact)\b/i, icon: Bandage },
  { match: /\b(allerg|pollen|dust|pollut|environ)\b/i, icon: Cloud },
  { match: /\b(hygien|sanit|cleanli|unclean|smegma)\b/i, icon: Sparkles },
  { match: /\b(surger|operat|post-op|previous)\b/i, icon: Scissors },
  { match: /\b(medication|drug|steroid|chemo|radiation)\b/i, icon: Syringe },
  { match: /\b(untreated|delayed|chronic|recurrent|repeat)\b/i, icon: AlertCircle },
  { match: /\b(stop|avoid|restric|cannot|unable|loss)\b/i, icon: Ban },
  { match: /\b(immun|autoimm|weak)\b/i, icon: ShieldOff },
  { match: /\b(test|diagnos|scan|biopsy|lab)\b/i, icon: Microscope },
  { match: /\b(consult|doctor|specialist|examin)\b/i, icon: Stethoscope },
  { match: /\b(temperatur|thermometer)\b/i, icon: Thermometer },
];

const FALLBACK_SYMPTOM_ICONS: LucideIcon[] = [Thermometer, CircleDot, Frown, Zap, Moon, Droplets];
const FALLBACK_CAUSE_ICONS: LucideIcon[] = [Dna, Utensils, HeartPulse, Hourglass, CigaretteOff, AlertCircle];

function pickIcon(text: string, fallback: LucideIcon): LucideIcon {
  for (const { match, icon } of KEYWORD_ICONS) {
    if (match.test(text)) return icon;
  }
  return fallback;
}

export function iconForSymptom(text: string, index: number): IconPick {
  const fallback = FALLBACK_SYMPTOM_ICONS[index % FALLBACK_SYMPTOM_ICONS.length]!;
  return { icon: pickIcon(text, fallback), tint: TINTS[index % TINTS.length]! };
}

export function iconForCause(text: string, index: number): IconPick {
  const fallback = FALLBACK_CAUSE_ICONS[index % FALLBACK_CAUSE_ICONS.length]!;
  return { icon: pickIcon(text, fallback), tint: TINTS[index % TINTS.length]! };
}
